import {DEFAULT_BACKGROUND_DATA} from "../data/backgrounds";
import {PLACEHOLDERS} from "../data/constants";
import {resolveBannerSelection, type BannerSelection} from "../data/template-availability";
import type {BgCatalogItem} from "../server/api/v3/backgrounds";
import {CONTENT_FIELDS, type LegacyScene} from "./legacy";
import {type CreateBannerInput} from "./defaults";

export async function getGridBaseScene(input: CreateBannerInput): Promise<LegacyScene> {
  const {product} = resolveBannerSelection(input);
  const placeholders = PLACEHOLDERS.en as Record<string, string>;
  const scene: LegacyScene = {base: "000", tags: {"tag-0": "Tag 1", "tag-1": "Tag 2"}};
  for (const [field, legacy] of Object.entries(CONTENT_FIELDS)) {
    const text = field === "productName" ? ("shortLabel" in product ? product.shortLabel : product.label)
      : placeholders[`${field}_${product.code}_${input.contentType}`] ?? placeholders[field] ?? "";
    scene[legacy] = {main: text};
  }
  return scene;
}

export type GridBackground = Pick<BgCatalogItem, "variation" | "imageUrl" | "miniUrl" | "outdatedUrl" | "thumbnailUrl" | "transforms" | "base" | "hasStoredTransforms">;
export type GridCatalogLoader = (channel: string, type: string, product: string) => Promise<GridBackground[]>;
export function hasGridBackgroundGallery(input: BannerSelection) {
  const {definition} = resolveBannerSelection(input);
  return definition.channel !== "ads" && !definition.templateId.startsWith("SpeakerPhoto");
}
export async function getGridBackgroundOptions(input: BannerSelection, getCatalog: GridCatalogLoader) {
  const {definition, product} = resolveBannerSelection(input);
  if (!hasGridBackgroundGallery(input)) return [];
  return (await getCatalog(definition.channel, input.contentType, product.code)).flatMap((item) => {
    const url = item.imageUrl || item.outdatedUrl;
    return url ? [{value: String(item.variation), url, previewUrl: item.thumbnailUrl || item.miniUrl || url, isOutdated: !item.imageUrl}] : [];
  });
}
export async function getGridBackgroundScene(input: CreateBannerInput, getCatalog: GridCatalogLoader): Promise<LegacyScene> {
  const {definition, product} = resolveBannerSelection(input);
  if (!hasGridBackgroundGallery(input) || input.background === "custom" || input.background === "generative") return {};
  const backgrounds = await getCatalog(definition.channel, input.contentType, product.code);
  const selected = backgrounds.find((item) => String(item.variation) === String(input.background));
  if (!selected || !(selected.imageUrl || selected.outdatedUrl)) throw new Error(`No background ${input.background} for ${product.label}`);
  const scene: LegacyScene = {customBg: selected.imageUrl || selected.outdatedUrl,
    bgX: selected.hasStoredTransforms ? {} : DEFAULT_BACKGROUND_DATA.x,
    bgY: selected.hasStoredTransforms ? {} : DEFAULT_BACKGROUND_DATA.y,
    bgScale: selected.hasStoredTransforms ? {} : DEFAULT_BACKGROUND_DATA.scale,
    bgRotate: {}, bgFlip: {},
  };
  if (selected.base !== null && selected.base !== undefined) scene.base = selected.base;
  for (const [id, transform] of Object.entries(selected.transforms)) {
    for (const [field, key] of [["bgX", "x"], ["bgY", "y"], ["bgScale", "scale"], ["bgRotate", "rotate"], ["bgFlip", "flip"]]) {
      scene[field] = {...scene[field] as object, [id]: transform[key]};
    }
  }
  return scene;
}
